import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    // Fires immediately on variant click — scrolls gallery without waiting for AJAX
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Fires immediately when variant is clicked (before/instead of AJAX).
   * Scrolls the slideshow to the variant's featured image by slide-id.
   * This is the reliable fix: works even when the AJAX fetch is aborted.
   *
   * @param {Event} event
   */
  #handleVariantSelected = (event) => {
    const resource = /** @type {{ id: string; featured_media?: { id: number } }} */ (/** @type {any} */ (event).detail?.resource);
    const featuredMediaId = resource?.featured_media?.id;
    if (!featuredMediaId) return;

    const slideshow = this.refs.slideshow;
    if (!slideshow) return;

    const slides = Array.from(slideshow.querySelectorAll('[slide-id]'));
    const targetIndex = slides.findIndex(
      (slide) => slide.getAttribute('slide-id') == String(featuredMediaId)
    );
    if (targetIndex === -1) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (typeof slideshow.select === 'function') {
          slideshow.select(targetIndex, undefined, { animate: false });
        } else {
          const scroller = /** @type {HTMLElement|null} */ (slideshow.querySelector('slideshow-slides'));
          if (scroller) scroller.scrollLeft = targetIndex * scroller.offsetWidth;
        }
      });
    });
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // In carousel mode, the gallery contains all images regardless of the variant.
    // The variant selection was already handled instantly by #handleVariantSelected.
    // Replacing the gallery HTML here is redundant and causes a visual flash when the new DOM resets to slide 0.
    if (this.dataset.presentation === 'carousel') {
      return;
    }

    const source = event.detail.data.html;
    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    const featuredMediaId = event.detail?.resource?.featured_media?.id;
    this.replaceWith(newMediaGallery);

    if (featuredMediaId) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = /** @type {HTMLElement & { select?: Function }} */ (newMediaGallery.querySelector('slideshow-component'));
          if (!el) return;
          const slides = Array.from(el.querySelectorAll('[slide-id]'));
          const idx = slides.findIndex((s) => s.getAttribute('slide-id') == String(featuredMediaId));
          if (idx === -1) return;
          if (typeof el.select === 'function') {
            el.select(idx, undefined, { animate: false });
          } else {
            const scroller = /** @type {HTMLElement|null} */ (el.querySelector('slideshow-slides'));
            if (scroller) scroller.scrollLeft = idx * scroller.offsetWidth;
          }
        });
      });
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
